import React, { forwardRef, memo } from 'react';
import CheckBox from '@lib/components/CheckBox/CheckBox';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import Typography, { Variant } from '@lib/components/Typography/Typography';
import { CheckboxSizes } from '@lib/components/CheckBox/enums';
import styles from './CheckBoxSingleBool.module.scss';

export enum CheckBoxWrapViews {
  row = 'row',
  list = 'list',
}

export interface CheckBoxGroupOption {
  label: string;
  value: string;
}

export interface CheckBoxSingleBoolProps {
  size?: CheckboxSizes;
  CheckBoxComponent?: React.JSX.ElementType;
  disabled?: boolean;
  itemClass?: string;
  label?: React.ReactNode;
  name: string;
  onChange: (v: boolean) => void;
  options: CheckBoxGroupOption[];
  value?: boolean;
  wrapClass?: string;
  wrapView?: CheckBoxWrapViews;
  labelTypographyProps?: {
    variant?: Variant;
    strong?: boolean;
  };
  iconProps?: {
    icon: string;
    iconChecked: string;
    symbolsOutlined?: boolean;
  };
  lockedProps?: {
    locked?: boolean;
    tooltip?: React.ReactNode;
  };
}

const CheckBoxSingleBool = forwardRef(
  (
    props: CheckBoxSingleBoolProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const {
      CheckBoxComponent = CheckBox,
      disabled,
      iconProps,
      itemClass,
      label,
      lockedProps,
      name: fieldName,
      onChange,
      options,
      value: fieldValue,
      wrapClass,
      wrapView,
      ...rest
    } = props;

    const onChangeHandler = (v: React.ChangeEvent<HTMLInputElement>) => {
      onChange(v.target.checked);
    };

    const showLockChip = !!lockedProps?.locked && !!disabled;

    if (showLockChip) {
      return (
        <div className={styles.lockedWrapper}>
          <Tooltip body={lockedProps?.tooltip}>
            <Chip
              type={ChipTypes.chipBlue}
              size={ChipSizes.small}
              leftIcon="lock"
              asBlockElement
            />
          </Tooltip>
          {label && <Typography variant="body">{label}</Typography>}
        </div>
      );
    }

    return (
      <CheckBoxComponent
        {...rest}
        checked={!!fieldValue}
        disabled={disabled}
        iconProps={iconProps}
        id={fieldName}
        label={label}
        name={fieldName}
        onChange={onChangeHandler}
      />
    );
  },
);

export default memo(CheckBoxSingleBool);
